import React from "react";
import Plotly from "plotly.js-dist-min";
import createPlotlyComponent from "react-plotly.js/factory";
import { dataUrlToBlob } from "../../utils/fileHelper";
import {
    copyGraphDataToClipboard,
    decodeObjectBdata,
} from "../../utils/graphUtils";
import "./MessageGraphs.css";

// assets
import copyActive from "../../assets/new-images/active-copy-icon.svg";
import copyInactive from "../../assets/new-images/copy-icon.svg";
import dataIcon from "../../assets/new-images/data-connector-icon.svg";
import downloadIcon from "../../assets/new-images/download-icon.svg";

const Plot = createPlotlyComponent(Plotly);
const normalizePieTrace = (trace) => {
    const labels = Array.isArray(trace?.labels) ? trace.labels : [];
    const values = Array.isArray(trace?.values) ? trace.values : [];
    const n = Math.max(labels.length, values.length);

    if (n === 0) return trace;

    const numericValues = values.map((v) => {
        const num = typeof v === "number" ? v : Number(v);
        return Number.isFinite(num) ? num : 0;
    });
    const total = numericValues.reduce((a, b) => a + b, 0);

    // If a pie has lots of categories or many tiny slices, Plotly will stack outside labels
    // into a long unreadable column. Default to inside percent labels and rely on hover for detail.
    const tooManySlices = n > 10;
    const hasTinySlices = total > 0 && numericValues.some((v) => v / total < 0.01);

    if (!tooManySlices && !hasTinySlices) return trace;

    return {
        ...trace,
        textinfo: "percent",
        textposition: "inside",
        insidetextorientation: "radial",
        hoverinfo: "label+percent+value",
        sort: false,
    };
};

const getCompactBarWidth = (dataLength) => {
    if (dataLength <= 3) return 0.55;
    if (dataLength <= 6) return 0.65;
    if (dataLength <= 10) return 0.58;
    return undefined;
};

const getCompactBargap = (dataLength) => {
    if (dataLength <= 6) return 0.16;
    if (dataLength <= 10) return 0.22;
    return undefined;
};

const getTraceDataLength = (trace) => trace?.x?.length || trace?.labels?.length || 0;

const hasCompactSmallBarChart = (data, variant) => (
    variant === "compact" &&
    Array.isArray(data) &&
    data.some((trace) => trace?.type === "bar" && getTraceDataLength(trace) <= 10)
);

const deepClone = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    try {
        if (typeof structuredClone === "function") {
            return structuredClone(obj);
        }
    } catch {
        // Fallback if structuredClone fails on any unsupported values
    }
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch {
        return { ...obj };
    }
};

const cleanGraphData = (data, layout, variant) => {
    if (!Array.isArray(data)) return data;

    const clonedData = deepClone(data);
    const decodedData = clonedData.map((trace) => decodeObjectBdata(trace));
    const isCompact = variant === "compact";

    const yAxisTitle = layout?.yaxis?.title?.text?.toLowerCase() || "";
    const xAxisTitle = layout?.xaxis?.title?.text?.toLowerCase() || "";
    const titleText = layout?.title?.text?.toLowerCase() || "";
    const isVolume =
        yAxisTitle.includes("kg") ||
        yAxisTitle.includes("volume") ||
        xAxisTitle.includes("kg") ||
        xAxisTitle.includes("volume") ||
        titleText.includes("kg") ||
        titleText.includes("volume");

    return decodedData.map((trace) => {
        const baseTrace = trace?.type === "pie" ? normalizePieTrace(trace) : trace;
        const newTrace = { ...baseTrace, cliponaxis: false };
        const dataLength = getTraceDataLength(trace);

        if (isVolume) {
            if (Array.isArray(newTrace.text)) {
                newTrace.text = newTrace.text.map((t) =>
                    typeof t === "string" ? t.replace(/\$/g, "") : t
                );
            }
            if (Array.isArray(newTrace.hovertext)) {
                newTrace.hovertext = newTrace.hovertext.map((t) =>
                    typeof t === "string" ? t.replace(/\$/g, "") : t
                );
            }
        }

        if (dataLength <= 12 && (newTrace.type === "scatter" || !newTrace.type)) {
            newTrace.textposition = Array(dataLength).fill("top center");
            newTrace.textposition[0] = "top right";
            newTrace.textposition[dataLength - 1] = "top left";
        }

        if (isCompact && newTrace.type === "bar") {
            const compactBarWidth = getCompactBarWidth(dataLength);
            if (compactBarWidth) {
                newTrace.width = compactBarWidth;
            }
        }

        if (dataLength > 12) {
            if (typeof newTrace.mode === "string") {
                newTrace.mode = newTrace.mode
                    .split("+")
                    .filter((m) => m !== "text")
                    .join("+");
                if (!newTrace.mode) newTrace.mode = "lines+markers";
            }
            if (newTrace.type === "bar") {
                newTrace.textposition = "none";
            }
        }
        return newTrace;
    });
};

// Normalizes the Plotly layout: fixes title spacing, disables zoom, makes axes
// readable, and styles the hover tooltip for better contrast.
const cleanGraphLayout = (layout, variant) => {
    const newLayout = deepClone(layout) || {};
    const isCompact = variant === "compact";

    if (isCompact) {
        delete newLayout.height;
    }

    if (isCompact && newLayout.title) {
        newLayout.title = typeof newLayout.title === "object"
            ? { ...newLayout.title }
            : { text: newLayout.title };
        const titleFont = typeof newLayout.title.font === "object" && newLayout.title.font
            ? newLayout.title.font
            : {};
        const titleFontSize = Number(titleFont.size);
        delete newLayout.title.y;
        newLayout.title.x = 0.015;
        newLayout.title.xanchor = "left";
        newLayout.title.pad = { ...(newLayout.title.pad || {}), b: 20 };
        newLayout.title.font = {
            ...titleFont,
            size: Math.min(Number.isFinite(titleFontSize) ? titleFontSize : 18, 18),
        };
    }

    if (isCompact && Array.isArray(newLayout.images)) {
        newLayout.images = newLayout.images.map((image) => ({
            ...image,
            xref: image.xref || "paper",
            yref: image.yref || "paper",
            x: 1,
            y: 1.16,
            xanchor: "right",
            yanchor: "top",
            sizex: Math.min(image.sizex || 0.12, 0.12),
            sizey: Math.min(image.sizey || 0.12, 0.12),
        }));
    }

    newLayout.xaxis = {
        ...(newLayout.xaxis || {}),
        fixedrange: true,
        automargin: true,
        showline: true,
        linecolor: "#cbd5e1",
        layer: "above traces",
        showgrid: false,
    };

    newLayout.yaxis = {
        ...(newLayout.yaxis || {}),
        fixedrange: true,
        automargin: true,
        showline: true,
        linecolor: "#cbd5e1",
        showgrid: true,
        gridcolor: "#e2e8f0",
        layer: "above traces",
    };

    newLayout.hoverlabel = {
        ...(newLayout.hoverlabel || {}),
        bgcolor: "#1a1a2e",
        font: { color: "#ffffff", size: 13, family: "Inter, sans-serif" },
        bordercolor: "transparent",
    };

    return newLayout;
};

const sanitizeFileName = (value) => Array.from(value)
    .filter((character) => (
        character.charCodeAt(0) >= 32 && !'<>:"/\\|?*'.includes(character)
    ))
    .join("");

const stripHtmlTags = (value) => {
    if (typeof value !== "string") return value;

    let previous;
    let current = value;
    do {
        previous = current;
        current = current.replace(/<[^>]*>/g, "");
    } while (current !== previous);

    return current;
};

const getGraphFileName = (graph, index, defaultName) => {
    const title = typeof graph?.layout?.title === "string"
        ? graph.layout.title
        : graph?.layout?.title?.text;
    const safeTitle = stripHtmlTags(title);
    const sanitizedTitle = safeTitle
        ? sanitizeFileName(safeTitle)
        .trim()
        .replace(/\s+/g, "_")
        : "";

    return `${sanitizedTitle || `${defaultName}-${index + 1}`}.png`;
};

const MessageGraphs = ({
    graph,
    variant = "default",
    showGraphActions = [],
    onGraphActionStatus,
}) => {
    const graphDivRefs = React.useRef([]);
    const [copiedGraphIndex, setCopiedGraphIndex] = React.useState(null);
    const [copiedDataIndex, setCopiedDataIndex] = React.useState(null);

    if (!graph) return null;

    const graphs = Array.isArray(graph) ? graph : [graph];
    const validGraphs = graphs.filter((g) => g && g.data);
    const graphActions = Array.isArray(showGraphActions) ? showGraphActions : [];
    const tableCopyAction = graphActions.find(
        (action) => action && typeof action === "object" && action.item === "table-copy"
    );
    const sourceGraphs = Array.isArray(tableCopyAction?.data)
        ? tableCopyAction.data.filter((g) => g && g.data)
        : tableCopyAction?.data?.data
            ? [tableCopyAction.data]
            : validGraphs;
    const hasGraphAction = (actionName) => graphActions.some(
        (action) => action === actionName || action?.item === actionName
    );

    if (validGraphs.length === 0) return null;

    const createGraphImage = async (index) => {
        const graphDiv = graphDivRefs.current[index];
        if (!graphDiv) throw new Error();

        return Plotly.toImage(graphDiv, {
            format: "png",
            width: Math.max(graphDiv.clientWidth, 800),
            height: Math.max(graphDiv.clientHeight, 450),
            scale: 2,
        });
    };

    const handleCopyGraph = async (index) => {
        try {
            if (!navigator.clipboard?.write || !window.ClipboardItem) {
                throw new Error();
            }

            const imageUrl = await createGraphImage(index);
            const imageBlob = dataUrlToBlob(imageUrl);
            await navigator.clipboard.write([
                new window.ClipboardItem({ "image/png": imageBlob }),
            ]);
            setCopiedGraphIndex(index);
            onGraphActionStatus?.("copy-success");
            window.setTimeout(() => setCopiedGraphIndex(null), 1500);
        } catch (error) {
            console.error("Failed to copy graph:", error);
            onGraphActionStatus?.("copy-error");
        }
    };

    const handleDownloadGraph = async (graphItem, index) => {
        try {
            const imageUrl = await createGraphImage(index);
            const link = document.createElement("a");
            link.href = imageUrl;
            link.download = getGraphFileName(graphItem, index, "graph");
            link.click();
            onGraphActionStatus?.("download-success");
        } catch (error) {
            console.error("Failed to download graph:", error);
            onGraphActionStatus?.("download-error");
        }
    };

    const handleCopyGraphData = async (index) => {
        try {
            const copied = await copyGraphDataToClipboard(
                sourceGraphs[index],
                tableCopyAction?.tableData
            );
            if (!copied) throw new Error();
            setCopiedDataIndex(index);
            onGraphActionStatus?.("data-copy-success");
            window.setTimeout(() => setCopiedDataIndex(null), 1500);
        } catch (error) {
            console.error("Failed to copy graph data:", error);
            onGraphActionStatus?.("data-copy-error");
        }
    };

    return (
        <div className="generated-graphs-container">
            {validGraphs.map((g, i) => {
                const clonedLayout = deepClone(g.layout);
                const cleanedData = cleanGraphData(g.data, clonedLayout, variant);
                const useCompactSmallBarLayout = hasCompactSmallBarChart(cleanedData, variant);
                const barTrace = useCompactSmallBarLayout
                    ? cleanedData.find((t) => t?.type === "bar")
                    : null;
                const barDataLength = barTrace ? getTraceDataLength(barTrace) : 0;

                return (
                    <div
                        key={i}
                        className="generated-graph"
                    >
                        {graphActions.length > 0 && (
                            <div className="generated-graph-actions">
                                {hasGraphAction("copy") && (
                                    <button
                                        type="button"
                                        className="generated-graph-action"
                                        onClick={() => handleCopyGraph(i)}
                                        title={copiedGraphIndex === i ? "Copied to clipboard!" : "Copy graph image"}
                                        aria-label={copiedGraphIndex === i ? "Copied to clipboard!" : "Copy graph image"}
                                    >
                                        <img
                                            src={copiedGraphIndex === i ? copyActive : copyInactive}
                                            alt=""
                                        />
                                    </button>
                                )}
                                {hasGraphAction("table-copy") && (
                                    <button
                                        type="button"
                                        className="generated-graph-action"
                                        onClick={() => handleCopyGraphData(i)}
                                        title={copiedDataIndex === i ? "Graph data copied!" : "Copy graph data"}
                                        aria-label={copiedDataIndex === i ? "Graph data copied!" : "Copy graph data"}
                                    >
                                        <img src={dataIcon} alt="" />
                                    </button>
                                )}
                                {hasGraphAction("download") && (
                                    <button
                                        type="button"
                                        className="generated-graph-action"
                                        onClick={() => handleDownloadGraph(g, i)}
                                        title="Download graph as PNG"
                                        aria-label="Download graph as PNG"
                                    >
                                        <img src={downloadIcon} alt="" />
                                    </button>
                                )}
                            </div>
                        )}
                        <Plot
                            data={cleanedData}
                            layout={{
                                ...cleanGraphLayout(clonedLayout, variant),
                                autosize: true,
                                bargap: useCompactSmallBarLayout ? (getCompactBargap(barDataLength) ?? 0.28) : clonedLayout?.bargap,
                                bargroupgap: useCompactSmallBarLayout ? 0.08 : clonedLayout?.bargroupgap,
                                hovermode: "closest",
                                margin: {
                                    l: 50,
                                    r: 25,
                                    t: variant === "compact" ? 72 : 64,
                                    b: Array.isArray(cleanedData) && cleanedData.some((t) => t?.type === "pie") ? 72 : 44,
                                    ...(clonedLayout?.margin || {}),
                                },
                                legend: Array.isArray(cleanedData) && cleanedData.some((t) => t?.type === "pie")
                                    ? {
                                        ...(clonedLayout?.legend || {}),
                                        orientation: "h",
                                        x: 0,
                                        y: -0.2,
                                    }
                                    : (clonedLayout?.legend ? { ...clonedLayout.legend } : undefined),
                            }}
                            useResizeHandler={true}
                            className="generated-graph-plot"
                            onInitialized={(_, graphDiv) => {
                                graphDivRefs.current[i] = graphDiv;
                            }}
                            onUpdate={(_, graphDiv) => {
                                graphDivRefs.current[i] = graphDiv;
                            }}
                            onPurge={() => {
                                graphDivRefs.current[i] = null;
                            }}
                            config={{
                                responsive: true,
                                displayModeBar: false,
                                doubleClick: false,
                                scrollZoom: false,
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default MessageGraphs;
